
"use client";

import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback, useMemo } from 'react';
import type { User, UserCredential } from 'firebase/auth';
import { 
  onAuthStateChanged, 
  signOut as firebaseSignOut, 
  GoogleAuthProvider, 
  signInWithPopup, 
  sendEmailVerification as firebaseSendEmailVerification, 
  applyActionCode as firebaseApplyActionCode, 
  reauthenticateWithCredential, 
  EmailAuthProvider, 
  updatePassword, 
  reauthenticateWithPopup, 
  deleteUser, 
  updateProfile, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { useRouter, usePathname } from 'next/navigation';
import { toast as globalToast } from "@/hooks/use-toast";
import { deleteUserAccountAndData, createUserDocument, updateUserDocument } from '@/lib/actions/user-actions';
import { getUserActiveSubscription } from "@/lib/actions/subscription-actions";
import { uploadAvatarAction } from '@/lib/actions/upload-actions';
import { trackSignupConversion } from '@/lib/analytics';

const ADMIN_USER_UIDS = (process.env.NEXT_PUBLIC_ADMIN_UID || "")
  .split(',')
  .map(uid => uid.trim())
  .filter(uid => uid);

if (typeof window !== 'undefined' && ADMIN_USER_UIDS.length === 0) {
  console.warn("Nenhum UID de administrador configurado em NEXT_PUBLIC_ADMIN_UID. Acesso à área /admin pode não funcionar como esperado.");
}

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  logout: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  login: (email: string, pass: string) => Promise<void>;
  signup: (email: string, pass: string, name: string) => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  isAdmin: boolean;
  sendVerificationEmail: () => Promise<void>;
  applyEmailVerificationCode: (actionCode: string) => Promise<void>;
  reloadUser: () => Promise<void>;
  deleteCurrentUserAccount: () => Promise<void>;
  updateUserProfile: (updates: { displayName?: string; avatarFile?: File }) => Promise<void>;
  updateUserPassword: (currentPass: string, newPass: string) => Promise<{ success: boolean; message: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  const handleRedirect = useCallback((user: User | null) => {
    if (!user) {
        router.push('/');
        return;
    }
    const userIsAdmin = ADMIN_USER_UIDS.includes(user.uid);
    const destination = userIsAdmin ? '/admin/dashboard' : '/dashboard';
    router.push(destination);
  }, [router]);
  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log(`[AUTH_CONTEXT] onAuthStateChanged: User is ${user ? user.uid : 'null'}. Verified: ${user?.emailVerified}`);
      setCurrentUser(user);
      setIsAdmin(user ? ADMIN_USER_UIDS.includes(user.uid) : false);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (loading) return;

    const isClientSide = typeof window !== 'undefined';
    const rootDomain = process.env.NEXT_PUBLIC_ROOT_DOMAIN || 'website.app.br';
    const isOnSubdomain = isClientSide && window.location.hostname !== `www.${rootDomain}` && window.location.hostname !== rootDomain && window.location.hostname !== 'localhost';

    if (isOnSubdomain) return;
    
    const isAppRoute = pathname.startsWith('/dashboard') || pathname.startsWith('/admin');
    const isVerificationPage = pathname.startsWith('/auth/verify-email');
    const isAuthActionPage = pathname.startsWith('/auth/action');
    const isForgotPasswordPage = pathname.startsWith('/forgot-password');

    if (!currentUser && isAppRoute) {
        console.log(`[AUTH_CONTEXT EFFECT] User on protected app page (${pathname}) without login. Redirecting to /.`);
        router.replace('/');
        return;
    }

    if (currentUser) {
        if (!currentUser.emailVerified && !isAdmin && !isVerificationPage && !isAuthActionPage) {
            console.log(`[AUTH_CONTEXT EFFECT] User on non-verification page (${pathname}) but not verified. Redirecting to /auth/verify-email.`);
            router.replace('/auth/verify-email');
            return;
        }

        if (pathname.startsWith('/admin') && !isAdmin) {
          console.log('[AUTH_CONTEXT EFFECT] Non-admin user on admin page. Redirecting to /dashboard.');
          globalToast({ title: "Acesso Negado", description: "Você não tem permissão para acessar esta área.", variant: "destructive" });
          router.replace('/dashboard');
          return;
        }
    }
  }, [currentUser, loading, isAdmin, pathname, router]);

  const logout = useCallback(async () => {
    try {
      await firebaseSignOut(auth);
      router.push('/');
    } catch (error) {
      console.error("[AUTH_CONTEXT] Erro ao fazer logout:", error);
      globalToast({ title: "Erro de Logout", description: "Não foi possível fazer logout.", variant: "destructive" });
    }
  }, [router]);

  const signInWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      if (user.email) {
        const isNewUser = (result as any).additionalUserInfo?.isNewUser ?? false;
        await createUserDocument(user.uid, user.email, user.displayName || '');
        if (isNewUser) {
            trackSignupConversion();
        }
      }
      globalToast({ title: "Login com Google bem-sucedido!", description: "Redirecionando..." });
      handleRedirect(user);
    } catch (error: any) {
      let errorMessage = "Ocorreu um erro ao tentar fazer login com Google.";
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = "A janela de login com Google foi fechada antes da conclusão.";
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        errorMessage = "Já existe uma conta com este e-mail usando um método de login diferente.";
      }
      globalToast({ title: "Erro de Login com Google", description: errorMessage, variant: "destructive" });
    }
  }, [handleRedirect]);
  
  const login = useCallback(async (email: string, pass: string): Promise<void> => {
    const userCredential = await signInWithEmailAndPassword(auth, email, pass);
    handleRedirect(userCredential.user);
  }, [handleRedirect]);

  const signup = useCallback(async (email: string, pass: string, name: string): Promise<void> => {
      const userCredential = await createUserWithEmailAndPassword(auth, email, pass);
      const user = userCredential.user;
      if (user) {
        await updateProfile(user, { displayName: name });
        await createUserDocument(user.uid, email, name);
        await firebaseSendEmailVerification(user); 
        trackSignupConversion();
        router.push('/auth/verify-email');
      }
  }, [router]);

  const sendPasswordReset = useCallback(async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  }, []);


  const sendVerificationEmail = useCallback(async () => {
    const userInstance = auth.currentUser;
    if (!userInstance) throw new Error("Usuário não logado para enviar e-mail de verificação.");
    await firebaseSendEmailVerification(userInstance);
  }, []);

  const applyEmailVerificationCode = useCallback(async (actionCode: string) => {
    await firebaseApplyActionCode(auth, actionCode);
  }, []);
  
  const reloadUser = useCallback(async () => {
    const userInstance = auth.currentUser;
    if (userInstance) {
      await userInstance.reload();
      setCurrentUser(auth.currentUser ? { ...auth.currentUser } : null);
      setIsAdmin(auth.currentUser ? ADMIN_USER_UIDS.includes(auth.currentUser.uid) : false);
    }
  }, []);

  const updateUserProfile = useCallback(async (updates: { displayName?: string; avatarFile?: File }) => {
    const user = auth.currentUser;
    if (!user) {
      globalToast({ title: "Erro", description: "Você não está logado.", variant: "destructive" });
      return;
    }
  
    const { displayName, avatarFile } = updates;
    const authUpdates: { displayName?: string; photoURL?: string } = {};
  
    try {
      if (avatarFile) {
        const formData = new FormData();
        formData.append('avatarFile', avatarFile);
        formData.append('userId', user.uid);
        
        const uploadResult = await uploadAvatarAction(formData);

        if (uploadResult.success && uploadResult.photoURL) {
          authUpdates.photoURL = uploadResult.photoURL;
        } else {
          throw new Error(uploadResult.message || "Falha no upload do avatar.");
        }
      }
  
      if (displayName !== undefined && displayName !== user.displayName) {
        authUpdates.displayName = displayName;
      }
      
      if (Object.keys(authUpdates).length > 0) {
        await updateProfile(user, authUpdates);
        await updateUserDocument(user.uid, { ...authUpdates });
        await reloadUser();
        globalToast({ title: "Sucesso!", description: "Perfil atualizado." });
      } else {
        globalToast({ title: "Nenhuma alteração", description: "Não havia alterações para salvar." });
      }
    } catch(error: any) {
      console.error("Error updating profile:", error);
      globalToast({ title: "Erro", description: `Não foi possível atualizar o perfil: ${error.message}`, variant: "destructive" });
    }
  }, [reloadUser]);
  
  const updateUserPassword = useCallback(async (currentPass: string, newPass: string): Promise<{ success: boolean; message: string }> => {
    const user = auth.currentUser;
    if (!user || !user.email) {
      return { success: false, message: "Usuário não autenticado." };
    }

    try {
      const credential = EmailAuthProvider.credential(user.email, currentPass);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPass);
      return { success: true, message: "Senha atualizada com sucesso!" };
    } catch (error: any) {
      let errorMessage = "Ocorreu um erro ao atualizar a senha.";
      if (error.code === 'auth/wrong-password') {
        errorMessage = "A senha atual está incorreta. Por favor, tente novamente.";
      } else if (error.code === 'auth/weak-password') {
        errorMessage = "A nova senha é muito fraca.";
      }
      return { success: false, message: errorMessage };
    }
  }, []);

  const deleteCurrentUserAccount = useCallback(async () => {
    const userToDelete = auth.currentUser;
    if (!userToDelete) {
        globalToast({ title: "Erro", description: "Nenhum usuário logado para excluir.", variant: "destructive" });
        return;
    }
    
    try {
        const activeSubscription = await getUserActiveSubscription(userToDelete.uid);
        if (activeSubscription && activeSubscription.planId !== 'free') {
            globalToast({
                title: "Assinatura Ativa Encontrada",
                description: "Você não pode excluir sua conta enquanto tiver uma assinatura paga ativa. Por favor, cancele sua assinatura na página de Faturamento primeiro.",
                variant: "destructive",
                duration: 10000,
            });
            return;
        }

        const providerId = userToDelete.providerData[0]?.providerId;

        if (providerId === 'password') {
            globalToast({ 
                title: "Ação de Segurança Necessária", 
                description: "Para excluir sua conta, por favor, faça logout e login novamente. Esta etapa confirma sua identidade recente.",
                variant: "destructive",
                duration: 10000 
            });
            return;
        } else if (providerId === 'google.com') {
            const provider = new GoogleAuthProvider();
            await reauthenticateWithPopup(userToDelete, provider);
            globalToast({ title: "Reautenticação bem-sucedida", description: "Prosseguindo com a exclusão da conta..." });
        } else {
            throw new Error("Seu método de login não suporta reautenticação automática para exclusão. Por favor, contate o suporte.");
        }

        const serverCleanupResult = await deleteUserAccountAndData(userToDelete.uid);
        if (!serverCleanupResult.success) {
            throw new Error(serverCleanupResult.message || "Falha ao limpar dados do servidor.");
        }

        await deleteUser(userToDelete);

        globalToast({ title: "Conta Excluída", description: "Sua conta e todos os seus dados foram excluídos permanentemente." });

    } catch (error: any) {
        let errorMessage = "Ocorreu um erro ao tentar excluir sua conta.";
        if (error.code === 'auth/requires-recent-login') {
            errorMessage = "Esta operação requer um login recente. Por favor, faça logout e login novamente antes de tentar excluir sua conta.";
        } else if (error.code === 'auth/popup-closed-by-user') {
            errorMessage = "A janela de reautenticação foi fechada antes da conclusão. A exclusão foi cancelada.";
        } else if (error.message) {
            errorMessage = error.message;
        }
        globalToast({ title: "Erro na Exclusão", description: errorMessage, variant: "destructive" });
    }
  }, []);

  const contextValue = useMemo(() => ({
    currentUser,
    loading,
    logout,
    signInWithGoogle,
    login,
    signup,
    sendPasswordReset,
    isAdmin,
    sendVerificationEmail,
    applyEmailVerificationCode,
    reloadUser,
    deleteCurrentUserAccount,
    updateUserProfile,
    updateUserPassword,
  }), [currentUser, loading, logout, signInWithGoogle, login, signup, sendPasswordReset, isAdmin, sendVerificationEmail, applyEmailVerificationCode, reloadUser, deleteCurrentUserAccount, updateUserProfile, updateUserPassword]);
  
  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};
